package data.network

import domain.repository.AuthenticationRepository
import io.ktor.client.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json

/**
 * Authorized HTTP client that handles authenticated requests to our API endpoints
 * Uses cookie-based authentication instead of Bearer tokens
 */
class AuthorizedHttpClient(
    private val authRepository: AuthenticationRepository
) {
    
    val client = HttpClient {
        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
                coerceInputValues = true
            })
        }
        
        // Install response validation to catch authentication errors
        HttpResponseValidator {
            validateResponse { response ->
                when (response.status) {
                    HttpStatusCode.Unauthorized -> {
                        println("🚫 AuthorizedClient: Received 401 Unauthorized, token is invalid")
                        // Launch coroutine to handle logout
                        GlobalScope.launch {
                            authRepository.handleInvalidToken()
                        }
                    }
                    HttpStatusCode.Forbidden -> {
                        println("🚫 AuthorizedClient: Received 403 Forbidden, checking if token-related")
                        // Check if it's a token issue based on response content or headers
                        val responseText = try {
                            response.bodyAsText()
                        } catch (e: Exception) {
                            ""
                        }
                        if (responseText.contains("token", ignoreCase = true) || 
                            responseText.contains("unauthorized", ignoreCase = true) ||
                            responseText.contains("authentication", ignoreCase = true)) {
                            println("🚫 AuthorizedClient: 403 appears to be token-related, logging out")
                            GlobalScope.launch {
                                authRepository.handleInvalidToken()
                            }
                        }
                    }
                }
            }
        }
    }
    
    /**
     * Make an authenticated request to our Vercel API endpoints
     * Uses cookie-based authentication instead of Bearer tokens
     */
    suspend fun authenticatedRequest(
        block: suspend HttpRequestBuilder.() -> Unit
    ): HttpResponse {
        // The HttpClient will automatically send cookies
        // Our API endpoints read the github_token cookie for auth
        return client.request {
            headers {
                append("Accept", "application/json")
                append("Content-Type", "application/json")
                append("User-Agent", "PR-Dashboard-App")
            }
            block()
        }
    }
    
    fun close() {
        client.close()
    }
}
